import React from 'react'
import { Element, Icon } from 'react-bulma-components'
import { WeeksSummaryStatus } from '../../api/missions'
import { MissionWeekValidatedIcon, WarningIcon, WorkerIcon } from '../../components/icons'
import { CheckCircleIcon } from '@heroicons/react/outline'

/**
 * Props pour le composant PayrollStats
 */
interface PayrollStatsProps {
  /** Statut des semaines pour tous les travailleurs */
  weeksSummaryStatus?: WeeksSummaryStatus
  /** Nombre total de travailleurs */
  totalWorkers: number
}

/**
 * Composant pour afficher les statistiques des périodes de paie
 *
 * Affiche le nombre de travailleurs dans chaque état :
 * - Clôturées (toutes les semaines enregistrées)
 * - Prêtes à être clôturées (toutes validées mais pas encore enregistrées)
 * - Avec validation manquante (semaines non validées)
 */
const PayrollStats: React.FC<PayrollStatsProps> = ({ weeksSummaryStatus, totalWorkers }) => {
  // Calculer les statistiques à partir des données
  const stats = React.useMemo(() => {
    if (!weeksSummaryStatus?.users) {
      return {
        closed: 0,
        readyToClose: 0,
        needsValidation: 0,
      }
    }

    let closed = 0
    let readyToClose = 0
    let needsValidation = 0

    weeksSummaryStatus.users.forEach(userStatus => {
      const allRecorded = userStatus.recordedWeeks === userStatus.weeks.length
      const allValidated = userStatus.validatedWeeks === userStatus.weeks.length

      if (allRecorded) {
        closed++
      } else if (allValidated) {
        readyToClose++
      } else {
        needsValidation++
      }
    })

    return {
      closed,
      readyToClose,
      needsValidation,
    }
  }, [weeksSummaryStatus])

  return (
    <Element data-test="payroll-workers-list-stats">
      {/* Périodes clôturées */}
      <Element mb={1} renderAs="span" mr={1}>
        <Icon.Text>
          <Icon color="success" size="small" mt={1}>
            <MissionWeekValidatedIcon />
          </Icon>
          <Element textSize={7} data-test="payroll-workers-list-stats-closed">
            {stats.closed} clôturée(s)
          </Element>
        </Icon.Text>
      </Element>

      {/* Périodes prêtes à être clôturées */}
      <Element mb={1} renderAs="span" mr={1}>
        <Icon.Text>
          <Icon color="warning" size="small" mt={1}>
            <CheckCircleIcon />
          </Icon>
          <Element textSize={7} data-test="payroll-workers-list-stats-ready-to-close">
            {stats.readyToClose} à clôturer
          </Element>
        </Icon.Text>
      </Element>

      {/* Périodes avec validation manquante */}
      <Element mb={1} renderAs="span" mr={1}>
        <Icon.Text>
          <Icon color="danger" size="small" mt={1}>
            <WarningIcon />
          </Icon>
          <Element textSize={7} data-test="payroll-workers-list-stats-needs-validation">
            {stats.needsValidation} à valider
          </Element>
        </Icon.Text>
      </Element>
    </Element>
  )
}

export default PayrollStats
